import { useState, useCallback } from "react"
import { useDropzone } from "react-dropzone"
import { upload_resource } from "../API"
import { AvailableResourceFormats, ResourceType } from "../Interfaces"

const MAX_FILE_SIZE = 1000 // Global variable to easily change the max file size (in MB)
const SUPPORTED_FILE_EXTENSIONS = ["pdf", "docx", "mp4", "jpg", "png"] as AvailableResourceFormats[]

interface UseResourceUploaderProps {
    type: ResourceType
    onResourceRefresh: () => void
}

interface UploadedFile {
    name: string
    type: string
    size: number
}

export function useResourceUploader({ type, onResourceRefresh }: UseResourceUploaderProps) {
    const [totalFiles, setTotalFiles] = useState<File[]>([])
    const [isUploading, setIsUploading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [uploadSuccess, setUploadSuccess] = useState(false)
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [uploadingCurrentFile, setUploadingCurrentFile] = useState<File | null>(null)
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])

    const onDrop = useCallback((acceptedFiles: File[]) => {
        setError(null)
        acceptedFiles.forEach((file) => {
            const validationError = validateFile(file, totalFiles, SUPPORTED_FILE_EXTENSIONS)
            if (validationError) {
                setError(validationError)
                return
            }
            setTotalFiles(prev => [...prev, file])
        })
    }, [totalFiles])

    const onRemove = (file: File) => {
        setTotalFiles(totalFiles.filter(f => f.name !== file.name))
    }

    const handleUpload = async () => {
        if (totalFiles.length === 0) {
            setError("No files selected")
            return
        }

        setIsUploading(true)
        setError(null)

        try {
            const successfullyUploadedFiles: UploadedFile[] = []

            for (const file of totalFiles) {
                setUploadingCurrentFile(file)
                setUploadSuccess(false)
                setError(null)
                await upload_resource(file, type, file.name)
                
                // Track successfully uploaded files
                successfullyUploadedFiles.push({
                    name: file.name,
                    type: file.name.split('.').pop()?.toLowerCase() || 'unknown',
                    size: file.size
                })
            }
            
            setUploadSuccess(true)
            setUploadedFiles(successfullyUploadedFiles)
            setTotalFiles([])
            
            // Keep the dialog open to show uploaded files list
            // User can manually close it or add descriptions
            onResourceRefresh()

            console.log("Uploaded files:", successfullyUploadedFiles)
            
        } catch (error: any) {
            console.error("Caught error:", error)
            switch (error?.response?.status) {
                case 400:
                    setError("Failed to upload files. Please try again.")
                    break
                case 401:
                    setError("You are not authorized to upload files. Please contact your administrator.")
                    break
                case 403:
                    setError("You are not authorized to upload files. Please contact your administrator.")
                    break
                case 404:
                    setError("File not found. Please try again.")
                    break
                case 500:
                    setError("Failed to upload files. Please try again.")
                    break
                default:
                    console.error("Error uploading files:", error)
                    setError("Failed to upload files. Please try again.")
            }
        } finally {
            setIsUploading(false)
        }
    }

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

    const resetDialog = () => {
        setTotalFiles([])
        setError(null)
        setUploadSuccess(false)
        setIsUploading(false)
        setUploadedFiles([])
        setUploadingCurrentFile(null)
    }

    const handleDialogChange = (open: boolean) => {
        setIsDialogOpen(open)
        if (!open) resetDialog()
    }

    const onClearFiles = () => setTotalFiles([])

    return {
        // State
        totalFiles,
        isUploading,
        error,
        uploadSuccess,
        isDialogOpen,
        uploadingCurrentFile,
        uploadedFiles,
        isDragActive,
        
        // Event handlers
        handleUpload,
        onRemove,
        onClearFiles,
        handleDialogChange,
        
        // Dropzone props
        getRootProps,
        getInputProps,
        
        // Constants
        MAX_FILE_SIZE,
        SUPPORTED_FILE_EXTENSIONS
    }
}

// File validation logic extracted into pure function
function validateFile(file: File, existingFiles: File[], supportedExtensions: string[]): string | null {
    const maxSize = MAX_FILE_SIZE * 1024 * 1024 // 500MB in bytes
    
    if (file.size > maxSize) {
        return `File ${file.name} is too large. Max size is ${MAX_FILE_SIZE}MB.`
    }

    const fileExtension = file.name.split('.').pop()?.toLowerCase()
    if (!fileExtension || !supportedExtensions.includes(fileExtension)) {
        return `File ${file.name} is not supported. Supported formats: ${supportedExtensions.join(', ')}`
    }

    const fileAlreadyExists = existingFiles.some(f => f.name === file.name)
    if (fileAlreadyExists) {
        return `File ${file.name} already exists.`
    }

    return null
}
